/**
 * ============================================================================
 * Iron Meridian - Country Tooltip Component
 * ============================================================================
 * 
 * Hover tooltip showing country stats at mouse position.
 * Premium glass-morphism design with smooth animations.
 * Includes terrain info with combat modifiers.
 */

import { memo } from 'react'

// Terrain types with icons and combat modifiers
const TERRAIN_DATA = {
  plains: { icon: '🌾', name: 'Plains', modifier: 0, desc: 'No combat modifier' },
  mountains: { icon: '⛰️', name: 'Mountains', modifier: 25, desc: '+25% defense' },
  forest: { icon: '🌲', name: 'Forest', modifier: 15, desc: '+15% defense' },
  desert: { icon: '🏜️', name: 'Desert', modifier: -10, desc: '-10% attacker penalty' },
  tundra: { icon: '❄️', name: 'Tundra', modifier: 10, desc: '+10% defense' },
  coastal: { icon: '🏖️', name: 'Coastal', modifier: 5, desc: 'Naval access bonus' },
  jungle: { icon: '🌴', name: 'Jungle', modifier: 20, desc: '+20% defense' },
  urban: { icon: '🏙️', name: 'Urban', modifier: 30, desc: '+30% defense' },
}

function CountryTooltip({ country, position, isPlayer }) {
  if (!country) return null

  // Position tooltip with offset from cursor
  const style = {
    left: position.x + 16,
    top: position.y + 16,
  }

  // Keep tooltip on screen with smooth repositioning
  if (typeof window !== 'undefined') {
    if (position.x > window.innerWidth - 300) {
      style.left = position.x - 280
    }
    if (position.y > window.innerHeight - 280) {
      style.top = position.y - 260
    }
  }

  // Get terrain data with fallback
  const terrain = TERRAIN_DATA[country.terrain] || TERRAIN_DATA.plains

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={style}
    >
      <div className="tooltip-container bg-war-room-800/95 backdrop-blur-md border border-war-room-600/50 rounded-xl shadow-2xl p-4 min-w-[260px] animate-tooltip-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-war-room-600/50">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm shadow-md ring-1 ring-white/10"
            style={{ backgroundColor: country.color }}
          >
            {country.id?.slice(0, 2) || '??'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white truncate">{country.name}</div>
            <div className="flex items-center gap-2">
              {isPlayer ? (
                <span className="text-xs text-war-room-accent font-medium flex items-center gap-1">
                  <span>👑</span> Your Empire
                </span>
              ) : (
                <span className="text-xs text-gray-400 capitalize">{country.controller}</span>
              )}
            </div>
          </div>
        </div>

        {/* Terrain Badge */}
        <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-war-room-700/50 rounded-lg">
          <span className="text-lg">{terrain.icon}</span>
          <div className="flex-1">
            <span className="text-sm text-gray-200 font-medium">{terrain.name}</span>
          </div>
          {terrain.modifier !== 0 && (
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
              terrain.modifier > 0 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {terrain.modifier > 0 ? '+' : ''}{terrain.modifier}%
            </span>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <MiniStat icon="⚔️" label="Military" value={country.military} color="#ef4444" />
          <MiniStat icon="💰" label="Economy" value={country.economy} color="#fbbf24" />
          <MiniStat icon="🔬" label="Tech" value={country.technology} color="#3b82f6" />
          {country.stability !== undefined && (
            <MiniStat icon="🏛️" label="Stability" value={country.stability} color="#22c55e" />
          )}
        </div>

        {/* Neighbors hint */}
        {country.neighbors && country.neighbors.length > 0 && (
          <div className="pt-2 border-t border-war-room-600/50">
            <div className="text-xs text-gray-500">
              <span className="text-gray-400">Borders:</span> {country.neighbors.slice(0, 4).join(', ')}
              {country.neighbors.length > 4 && <span className="text-gray-500"> +{country.neighbors.length - 4} more</span>}
            </div>
          </div>
        )}

        {/* Click hint */}
        <div className="mt-2 text-xs text-gray-600 text-center">
          Click for details
        </div>
      </div>
    </div>
  )
}

// Memoize MiniStat to prevent re-renders
const MiniStat = memo(function MiniStat({ icon, label, value, color }) {
  return (
    <div className="bg-war-room-700/50 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
      <span className="text-sm">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm tabular-nums" style={{ color }}>{value}</div>
        <div className="text-xs text-gray-500 truncate">{label}</div>
      </div>
    </div>
  )
})

// Memoize tooltip to prevent unnecessary re-renders during rapid mouse movement
export default memo(CountryTooltip)
